import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { fetchStudentClasses, fetchStudentAttendance, fetchStudentMarks } from "../../services/sheetDataService";

const daysOfWeek = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday"
];

const StudentDashboard = ({ showWelcome = true }) => {
  const { user, accessToken } = useAuth();
  const [message, setMessage] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [marks, setMarks] = useState([]);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [activeTab, setActiveTab] = useState('class');

  // Helper: get the correct spreadsheet IDs
  const getUsersSheetId = () => user.usersSpreadsheetId || user.spreadsheetId;
  const getAttendanceSheetId = () => user.attendanceSpreadsheetId || user.spreadsheetId;
  const getMarksSheetId = () => user.marksSpreadsheetId || user.spreadsheetId;

  // Fetch student's classes
  useEffect(() => {
    const loadClasses = async () => {
      if (!user?.userId || !user?.spreadsheetId || !accessToken) {
        setLoadingClasses(false);
        return;
      }
      try {
        const usersSheetId = getUsersSheetId();
        const clsList = await fetchStudentClasses(accessToken, usersSheetId, user.userId);
        setClasses(clsList);
        if (clsList.length > 0) setSelectedClass(clsList[0]);
      } catch (error) {
        console.error("Failed to load classes:", error);
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };
    loadClasses();
  }, [user?.userId, user, accessToken]);

  // Fetch attendance when selected class changes
  useEffect(() => {
const loadAttendance = async () => {
  if (!selectedClass || !user?.userId || !accessToken) return;
  setLoadingAttendance(true);
  try {
    const attendanceSheetId = getAttendanceSheetId();
    console.log('📋 StudentDashboard attendance params:', {
      attendanceSheetId,
      userId: user.userId,
      teacher: selectedClass.teacher,
      subject: selectedClass.subject,
      grade: selectedClass.grade
    });
    const records = await fetchStudentAttendance(
      accessToken,
      attendanceSheetId,
      user.userId,
      selectedClass.teacher,
      selectedClass.subject,
      selectedClass.grade
    );
    console.log('📋 Records returned:', records);
    setAttendance(records);
  } catch (err) {
    console.error('Error loading attendance:', err);
  } finally {
    setLoadingAttendance(false);
  }
};
    loadAttendance();
  }, [selectedClass, user?.userId, accessToken, user]);

  // Fetch marks when selected class changes
  useEffect(() => {
    const loadMarks = async () => {
      if (!selectedClass || !user?.userId || !accessToken) return;
      setLoadingMarks(true);
      try {
        const marksSheetId = getMarksSheetId();
        const records = await fetchStudentMarks(
          accessToken,
          marksSheetId,
          user.userId,
          selectedClass.teacher,
          selectedClass.subject,
          selectedClass.grade
        );
        setMarks(records);
      } catch (err) {
        console.error('Error loading marks:', err);
      } finally {
        setLoadingMarks(false);
      }
    };
    loadMarks();
  }, [selectedClass, user?.userId, accessToken, user]);

  const now = new Date();
  const today = now.toLocaleDateString("en-US", { weekday: "long" });

  // Helper: get the current/next session based on current time
  const getActiveSession = (schedule) => {
    if (!schedule || schedule.length === 0) return null;
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Check if any session is today and has started
    const todaySessions = schedule.filter(s => s.day === today);
    const active = todaySessions.find(s => {
      const [hours, minutes] = s.startTime.split(':').map(Number);
      const sessionStart = hours * 60 + minutes;
      const sessionEnd = sessionStart + s.duration;
      return currentTime >= sessionStart && currentTime < sessionEnd;
    });
    if (active) return active;

    // If no active session, return the next upcoming session (today or future)
    // We'll just return the first session of the day if any
    if (todaySessions.length > 0) {
      // Return the first today session (or the one that starts soonest)
      return todaySessions.sort((a, b) => {
        const [ha, ma] = a.startTime.split(':').map(Number);
        const [hb, mb] = b.startTime.split(':').map(Number);
        return (ha * 60 + ma) - (hb * 60 + mb);
      })[0];
    }
    return null;
  };

  const handleJoinClass = (cls) => {
    if (!cls) return;
    const schedule = cls.schedule || [];
    if (schedule.length === 0) {
      setMessage(`No schedule set for ${cls.subject}.`);
      return;
    }

    const active = getActiveSession(schedule);
    if (!active) {
      // Check if there is a session today
      const todaySessions = schedule.filter(s => s.day === today);
      if (todaySessions.length > 0) {
        // Find the next session today
        const next = todaySessions.sort((a, b) => {
          const [ha, ma] = a.startTime.split(':').map(Number);
          const [hb, mb] = b.startTime.split(':').map(Number);
          return (ha * 60 + ma) - (hb * 60 + mb);
        })[0];
        setMessage(`Your ${cls.subject} class is today at ${next.startTime} (${next.duration} mins).`);
      } else {
        const first = schedule[0];
        setMessage(`Your ${cls.subject} class is on ${first.day} at ${first.startTime} (${first.duration} mins).`);
      }
      return;
    }

    // Active session found – join via Zoom if available
    if (cls.zoomLink) {
      window.open(cls.zoomLink, "_blank");
      setMessage(`Joining ${cls.subject}...`);
      setTimeout(() => setMessage(null), 3000);
    } else {
      setMessage(`Zoom link not available for ${cls.subject}.`);
    }
  };

  const handleGoogleClassroom = (cls) => {
    if (cls.googleClassroomLink) {
      window.open(cls.googleClassroomLink, "_blank");
    } else {
      setMessage(`Google Classroom link not available for ${cls.subject}.`);
    }
  };

  if (loadingClasses) {
    return <div className="text-center py-10">Loading your classes...</div>;
  }

  if (!user) {
    return <p className="text-center py-10">Please log in to view your dashboard.</p>;
  }

  if (!user.role) {
    return (
      <div className="text-center py-10">
        <h1 className="text-2xl font-bold mb-4">Hello, {user.name}</h1>
        <p className="text-lg text-red-500">
          Your account is missing a role. Please contact the admin.
        </p>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <h1 className="text-3xl font-bold mb-6 text-center">Welcome, {user?.name}</h1>
        <div className="bg-white rounded shadow p-6 text-center">
          <p className="text-gray-500">You are not enrolled in any class.</p>
        </div>
      </div>
    );
  }

  const currentClass = selectedClass || classes[0];
  const schedule = currentClass.schedule || [];
  const hasTodaySession = schedule.some(s => s.day === today);

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {showWelcome && (
        <h1 className="text-3xl font-bold mb-6 text-center">
          Welcome, {user?.name}
        </h1>
      )}

      {message && (
        <div className="mb-6 p-3 bg-yellow-100 text-yellow-800 rounded text-center">
          {message}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4">
        {/* Sidebar */}
        <div className="md:w-1/4 bg-white p-4 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">My Class</h2>

          {/* Subject Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Subject</label>
            <select
              value={currentClass.subject}
              onChange={(e) => {
                const selected = classes.find(c => c.subject === e.target.value);
                setSelectedClass(selected);
              }}
              className="w-full border rounded px-2 py-1"
            >
              {classes.map(cls => (
                <option key={cls.classId} value={cls.subject}>
                  {cls.subject} ({cls.grade})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <p><strong>Subject:</strong> {currentClass.subject}</p>
            <p><strong>Grade:</strong> {currentClass.grade}</p>
            <p><strong>Teacher:</strong> {currentClass.teacher || 'N/A'}</p>
            {schedule.length > 0 ? (
              <div>
                <strong>Sessions:</strong>
                <ul className="list-disc list-inside text-sm">
                  {schedule.map((s, idx) => (
                    <li key={idx}>{s.day} at {s.startTime} ({s.duration} mins)</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p><strong>Sessions:</strong> No schedule set</p>
            )}
            <hr />
            <button
              onClick={() => handleJoinClass(currentClass)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Join Class
            </button>
            <button
              onClick={() => handleGoogleClassroom(currentClass)}
              className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mt-2"
            >
              Google Classroom
            </button>
            {currentClass.zoomLink && (
              <a
                href={currentClass.zoomLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-blue-600 hover:underline truncate"
              >
                Zoom Link
              </a>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="md:w-3/4 bg-white rounded shadow p-4">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="flex space-x-4">
              {['class', 'attendance', 'marks'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-3 capitalize ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'class' ? 'Class Schedule' : tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          {activeTab === 'class' && (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">
                  Today ({today}) – {currentClass.subject}
                </h2>
                {hasTodaySession ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {schedule.filter(s => s.day === today).map((s, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleJoinClass(currentClass)}
                        className="cursor-pointer p-5 border border-green-700 rounded-lg shadow hover:shadow-lg transition hover:scale-105"
                      >
                        <h3 className="text-xl font-semibold">{currentClass.subject}</h3>
                        <p className="text-gray-600">
                          {s.startTime} ({s.duration} mins)
                        </p>
                        <p className="text-sm text-blue-600 hover:underline mt-2">Click to join class</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No class today.</p>
                )}
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Weekly Timetable – {currentClass.subject}</h2>
                <div className="grid md:grid-cols-5 gap-4">
                  {daysOfWeek.map((day) => {
                    const daySessions = schedule.filter(s => s.day === day);
                    return (
                      <div key={day} className="border border-green-500 rounded-lg p-3">
                        <h3 className="font-bold mb-3 text-center">{day}</h3>
                        {daySessions.length > 0 ? (
                          daySessions.map((s, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleJoinClass(currentClass)}
                              className="cursor-pointer mb-2 p-2 bg-gray-100 rounded hover:bg-gray-200 transition"
                            >
                              <p className="font-medium pb-2">{currentClass.subject}</p>
                              <p className="text-sm text-gray-600">Start: {s.startTime}</p>
                              <p className="text-sm text-gray-600">Duration: {s.duration} mins</p>
                              <p
                                className="text-sm text-blue-600 hover:underline py-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGoogleClassroom(currentClass);
                                }}
                              >
                                Visit: Google Classroom
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-400 text-center">No classes</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          {activeTab === 'attendance' && (
            <section>
              <h2 className="text-2xl font-semibold mb-4">
                Attendance – {currentClass.subject} ({currentClass.grade})
              </h2>
              {loadingAttendance ? (
                <p className="text-center py-4">Loading attendance...</p>
              ) : attendance.length === 0 ? (
                <p className="text-center text-gray-500">No attendance records found for this subject.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-4 py-2 text-left">Date</th>
                        <th className="border px-4 py-2 text-left">Status</th>
                        <th className="border px-4 py-2 text-left">Last Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.map((rec, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="border px-4 py-2">{new Date(rec.date).toLocaleDateString()}</td>
                          <td className="border px-4 py-2">
                            <span className={`px-2 py-1 rounded ${
                              rec.status === 'Present' 
                                ? 'bg-green-200 text-green-800' 
                                : 'bg-red-200 text-red-800'
                            }`}>
                              {rec.status}
                            </span>
                          </td>
                          <td className="border px-4 py-2">
                            {rec.lastModified ? new Date(rec.lastModified).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {activeTab === 'marks' && (
            <section>
              <h2 className="text-2xl font-semibold mb-4">
                Marks – {currentClass.subject} ({currentClass.grade})
              </h2>
              {loadingMarks ? (
                <p className="text-center py-4">Loading marks...</p>
              ) : marks.length === 0 ? (
                <p className="text-center text-gray-500">No marks available for this subject.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-4 py-2 text-left">Type</th>
                        <th className="border px-4 py-2 text-left">Assessment Name</th>
                        <th className="border px-4 py-2 text-left">Term</th>
                        <th className="border px-4 py-2 text-left">Marks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marks.map((rec, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="border px-4 py-2">{rec.type}</td>
                          <td className="border px-4 py-2">{rec.name}</td>
                          <td className="border px-4 py-2">{rec.term}</td>
                          <td className="border px-4 py-2 font-semibold">{rec.marks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;